// File: src/components/reveal/RevealInstructionsBottomSheet.jsx
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { LuClock, LuMessageSquare, LuShieldCheck } from 'react-icons/lu';
import { ReactComponent as Step1Illustration } from '../../assets/Illustrations/Waiting 4 Streamline Milano.svg';
import { ReactComponent as Step2Illustration } from '../../assets/Illustrations/Tracking Users 2 Streamline Milano.svg';
import { ReactComponent as Step3Illustration } from '../../assets/Illustrations/Business Deal 2 Streamline Milano.svg';

const isPWA = () =>
  (window.matchMedia?.('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true;

export default function RevealInstructionsBottomSheet({ open = false, onClose }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const scrollRef = useRef(0);

  useEffect(() => {
    if (open) setCurrentSlide(0);
  }, [open]);

  /* Socket heartbeat (mirror mixer for parity) */
  const userId = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;
  useEffect(() => {
    if (!open) return;
    const socket = io(`${process.env.REACT_APP_API_URL}`);
    socket.on('connect', () =>
      console.log('[CLIENT] RevealInstructionsBottomSheet connected =>', socket.id)
    );
    const timerId = setInterval(() => {
      if (navigator.geolocation && typeof navigator.geolocation.getCurrentPosition === 'function' && userId) {
        navigator.geolocation.getCurrentPosition((pos) => {
          const sessionId = localStorage.getItem('sessionId');
          socket.emit('locationUpdate', {
            userId,
            sessionId,
            latitude: pos?.coords?.latitude,
            longitude: pos?.coords?.longitude,
          });
        });
      }
    }, 60_000);
    return () => {
      clearInterval(timerId);
      socket.disconnect();
    };
  }, [userId, open]);

  /* Overlay show/hide + theme color (parity with mixer) */
  useEffect(() => {
    if (!open) return;
    const meta = document.querySelector('meta[name="theme-color"]');
    const yNow = window.scrollY || window.pageYOffset;
    scrollRef.current = yNow;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${yNow}px`;
    document.body.classList.add('overlay-active');
    document.documentElement.classList.add('overlay-active');
    document.getElementById('root')?.classList.add('overlay-active');
    if (meta) meta.content = 'https://chart.googleapis.com/chart?chst=d_text_outline&chld=48|000000|h|000000|b|Error'; // placeholder to trigger rebuild
    return () => {
      const y = scrollRef.current || 0;
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root')?.classList.remove('overlay-active');
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo(0, y);
      const m = document.querySelector('meta[name="theme-color"]');
      if (m) m.content = '#ffffff';
    };
  }, [open]);

  /* Slides: titles/illustrations per request, no grey card in hero */
  const slides = useMemo(
    () => [
      {
        // Icon kept for parity, not used in hero
        Icon: LuClock,
        Illustration: Step1Illustration,
        title: 'Wait for reveal',
        description: 'When the countdown hits zero, your match appears here in the app.',
        accent: '#111827',
        badgeBg: 'transparent',
      },
      {
        Icon: LuMessageSquare,
        Illustration: Step2Illustration,
        title: 'Navigate & verify',
        description: 'Follow the map to meet your match, then enter the code to start your session.',
        accent: '#0b0b0b',
        badgeBg: 'transparent',
      },
      {
        Icon: LuShieldCheck,
        Illustration: Step3Illustration,
        title: 'Enjoy conversation',
        description: 'Jump into your 10-minute chat and get to know each other.',
        accent: '#000000',
        badgeBg: 'transparent',
      },
    ],
    []
  );

  const totalSlides = slides.length;
  const isLast = currentSlide === totalSlides - 1;
  const current = slides[currentSlide];

  /* Navigation */
  const handleClose = useCallback(() => onClose?.(), [onClose]);
  const handlePrev = useCallback(() => {
    if (currentSlide === 0) handleClose();
    else setCurrentSlide((p) => p - 1);
  }, [currentSlide, handleClose]);
  const handleSkip = useCallback(() => { handleClose(); }, [handleClose]);
  const handleNextOrDone = useCallback(() => {
    if (!isLast) setCurrentSlide((p) => p + 1);
    else handleClose();
  }, [isLast, handleClose]);

  /* Swipe + keyboard (parity) */
  const touchStartX = useRef(null);
  const onTouchStart = (e) => (touchStartX.current = e.touches?.[0]?.clientX ?? null);
  const onTouchEnd = (e) => {
    const start = touchStartX.current;
    const end = e.changedTouches?.[0]?.clientX ?? null;
    if (start == null || end == null) return;
    const delta = end - start;
    if (Math.abs(delta) > 40) {
      if (delta > 0) handlePrev();
      else handleNextOrDone();
    }
  };
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNextOrDone();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, handleClose, handlePrev, handleNextOrDone]);

  if (!open) return null;

  const progress = (currentSlide + 1) / totalSlides;

  return (
    <div style={S.backdrop} onClick={handleSkip}>
      <div
        style={{
          ...S.sheet,
          paddingBottom: `calc(${isPWA() ? 20 : 12}px + env(safe-area-inset-bottom, 0px))`,
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Reveal quick tour"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Top bar */}
        <div style={S.topBar}>
          <button
            type="button"
            onClick={handlePrev}
            aria-label={currentSlide === 0 ? 'Close' : 'Back'}
            style={S.iconBtn}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>
              {currentSlide === 0 ? (
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              ) : (
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
              )}
            </svg>
          </button>
          <div style={S.stepPill}>
            <span style={S.stepPillPrefix}>Step&nbsp;</span>
            <strong style={S.stepPillNum}>{currentSlide + 1}</strong>
            <span style={S.stepPillTotal}>&nbsp;/&nbsp;{totalSlides}</span>
          </div>
          <button type="button" onClick={handleSkip} style={S.skipLink}>
            Skip
          </button>
        </div>

        {/* Progress bar */}
        <div style={S.progressTrack} aria-hidden>
          <div style={{ ...S.progressFill, width: `${progress * 100}%` }} />
        </div>

        {/* Hero illustration (width 275, no grey card) */}
        <div style={S.hero}>
          <current.Illustration style={{ width: 275, height: 'auto', display: 'block' }} />
        </div>

        {/* Copy */}
        <div style={S.copy}>
          <h3 style={S.title}>{current.title}</h3>
          <p style={S.desc}>{current.description}</p>
        </div>

        {/* CTA */}
        <div style={S.ctaRow}>
          <button type="button" onClick={handleNextOrDone} style={S.primaryBtn}>
            {isLast ? 'Got it' : 'Next'}
          </button>
        </div>

        <style
          dangerouslySetInnerHTML={{
            __html: `
              @media (prefers-reduced-motion: no-preference) {
                .tour-fade-in { animation: fadeIn .28s ease-out; }
              }
              @keyframes fadeIn {
                from { opacity: 0; transform: translateY(8px) }
                to   { opacity: 1; transform: translateY(0) }
              }
            `,
          }}
        />
      </div>
    </div>
  );
}

/* Styles (identical tokens to mixer for pixel parity) */
const S = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: '#0006',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  sheet: {
    width: '100%',
    maxWidth: 440,
    margin: '0 10px 10px',
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 40,
  },
  iconBtn: {
    background: 'transparent',
    border: 0,
    width: 32,
    height: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    padding: 0,
    color: '#0b0b0b',
    borderRadius: 8,
    cursor: 'pointer',
  },
  skipLink: {
    background: 'transparent',
    border: 0,
    padding: '8px 10px',
    color: '#6b7280',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: 8,
  },
  stepPill: {
    background: '#f3f4f6',
    color: '#0b0b0b',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    lineHeight: 1,
  },
  stepPillPrefix: { fontWeight: 500, color: '#6b7280' },
  stepPillNum: { fontWeight: 800 },
  stepPillTotal: { color: '#6b7280', fontWeight: 600 },

  progressTrack: {
    height: 3,
    background: '#eef0f2',
    borderRadius: 3,
    overflow: 'hidden',
    marginTop: 12,
  },
  progressFill: {
    height: '100%',
    background: '#0b0b0b',
    transition: 'width .25s ease',
  },

  hero: {
    marginTop: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  copy: {
    textAlign: 'left',
    padding: '14px 2px 0',
  },
  title: {
    fontSize: 20,
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
    fontWeight: 800,
    color: '#0b0b0b',
    margin: 0,
  },
  desc: {
    marginTop: 6,
    marginBottom: 0,
    fontSize: 14,
    lineHeight: 1.5,
    color: '#4b5563',
  },

  ctaRow: {
    display: 'flex',
    gap: 8,
    marginTop: 8,
  },
  primaryBtn: {
    flex: 1,
    background: '#0b0b0b',
    color: '#fff',
    border: '1px solid #0b0b0b',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
    marginTop: 20,
  },
};
